import { captchaConfigured, RECAPTCHA } from '@/content/recaptcha';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

/**
 * reCAPTCHA v3, loaded only where it is used.
 *
 * The script is injected from the contact page rather than from index.html, so
 * every other route on the site still makes zero third-party requests. Someone
 * who reads the home page and leaves is never announced to Google.
 *
 * Nothing here ever rejects. A visitor who blocks Google, is behind a firewall
 * that does, or is simply offline gets a null token, and the decision about
 * whether that is acceptable belongs to the backend - `submit_form()` enforces
 * the site's captcha policy. Failing loudly in the browser would just turn
 * Google being unreachable into a form that cannot be submitted at all, with no
 * server-side say in the matter.
 */

const SCRIPT_URL = 'https://www.google.com/recaptcha/api.js';

/**
 * Ceiling on the whole token round trip - script load, ready, execute.
 *
 * Not paranoia. A script request that is dropped rather than refused never fires
 * `error`, and `grecaptcha.execute` has no timeout of its own, so without this
 * the promise can simply never settle: the submit button stays disabled and the
 * form reads "Sending..." forever, with no error and nothing to retry. A captive
 * portal or a firewall that blackholes google.com produces exactly that.
 *
 * Five seconds, then give up and submit without a token, which is a decision the
 * backend gets to make rather than a dead end in the browser.
 */
const TOKEN_TIMEOUT_MS = 5_000;

let pending: Promise<void> | null = null;

/** Resolves with `fallback` if `promise` has not settled in time. Never rejects. */
function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise<T>((resolve) => {
    const timer = setTimeout(() => {
      resolve(fallback);
    }, ms);

    const settle = (value: T) => {
      clearTimeout(timer);
      resolve(value);
    };

    promise.then(settle).catch(() => {
      settle(fallback);
    });
  });
}

/**
 * Injects api.js once. Safe to call repeatedly - later calls await the first.
 *
 * Worth calling early (on mount) so the round trip to Google is already done by
 * the time someone presses send.
 */
export function loadCaptcha(): Promise<void> {
  if (!captchaConfigured()) return Promise.resolve();
  if (window.grecaptcha) return Promise.resolve();
  if (pending) return pending;

  pending = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${SCRIPT_URL}?render=${encodeURIComponent(RECAPTCHA.siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      resolve();
    };
    script.onerror = () => {
      reject(new Error('reCAPTCHA script failed to load'));
    };
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    // Clear the cached failure so a later submit can retry rather than being
    // permanently poisoned by one blocked request.
    pending = null;
    throw error;
  });

  return pending;
}

/**
 * Returns a fresh token, or null if one could not be obtained for any reason.
 *
 * Tokens are single-use and expire after two minutes, so this is called at the
 * moment of the action rather than held from page load.
 *
 * `action` is what the score gets attributed to in Google's console; it defaults
 * to the enquiry so existing callers keep their behaviour.
 */
export function getCaptchaToken(action: string = RECAPTCHA.action): Promise<string | null> {
  if (!captchaConfigured()) return Promise.resolve(null);
  return withTimeout(mintToken(action), TOKEN_TIMEOUT_MS, null);
}

async function mintToken(action: string): Promise<string | null> {
  try {
    await loadCaptcha();

    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) return null;

    await new Promise<void>((resolve) => {
      grecaptcha.ready(resolve);
    });

    const token = await grecaptcha.execute(RECAPTCHA.siteKey, { action });

    return token || null;
  } catch {
    return null;
  }
}
